export type NotificationImportance = 'important' | 'social'
export type NotificationKind =
  | 'agenda_changed'
  | 'position_request'
  | 'sync_pending'
  | 'poll'
  | 'deadline'
  | 'challenge'
  | 'reveal'
  | 'photo'
  | 'interaction'

export type PushStatus = 'disabled' | 'default' | 'granted' | 'denied' | 'unavailable' | 'requires_install' | 'configuration_missing'
export type PushCapability = 'unavailable' | 'requires_install' | 'default' | 'granted' | 'denied'

export type NotificationPayload = {
  id: string
  kind: NotificationKind
  importance: NotificationImportance
  title: string
  body: string
  deepLink: string
}

export type NotificationPreference = {
  importantEnabled: true
  socialEnabled: boolean
  pushStatus: PushStatus
}

type CapabilityInput = {
  notificationPermission: NotificationPermission
  hasNotifications: boolean
  hasServiceWorker: boolean
  hasPushManager: boolean
  isIos: boolean
  isStandalone: boolean
}

const allowedDeepLinks = /^\/(?:$|agenda(?:[/?#]|$)|carte(?:[/?#]|$)|groupe(?:[/?#]|$)|souvenirs(?:[/?#]|$)|pratique(?:[/?#]|$))/u
const sensitiveText = /(activation[_ -]?secret|service[_ -]?role|trip[_ -]?code|code du voyage|\bpin\s*[:= ]?\s*\d{4}\b|contenu secret|secret non révélé|unrevealed secret|-?\d{1,3}\.\d{4,}\s*,\s*-?\d{1,3}\.\d{4,})/iu

function safeText(value: string, label: string, maximum: number): string {
  const trimmed = value.trim()
  if (trimmed.length < 1 || trimmed.length > maximum) throw new Error(`${label} de notification invalide.`)
  if (sensitiveText.test(trimmed)) throw new Error('Le contenu de notification contient une donnée sensible ou un secret.')
  return trimmed
}

export function validateNotificationPayload(payload: NotificationPayload): Readonly<NotificationPayload> {
  if (!allowedDeepLinks.test(payload.deepLink) || payload.deepLink.startsWith('//')) {
    throw new Error('Le lien de notification doit rester interne à Caravan.')
  }
  return Object.freeze({
    id: payload.id,
    kind: payload.kind,
    importance: payload.importance,
    title: safeText(payload.title, 'Titre', 90),
    body: safeText(payload.body, 'Texte', 240),
    deepLink: payload.deepLink,
  })
}

export function normalizeNotificationPreference(input: {
  socialEnabled: boolean
  pushStatus: PushStatus
}): NotificationPreference {
  return { importantEnabled: true, socialEnabled: input.socialEnabled, pushStatus: input.pushStatus }
}

export function detectPushCapability(input: CapabilityInput): PushCapability {
  if (!input.hasNotifications || !input.hasServiceWorker || !input.hasPushManager) return 'unavailable'
  if (input.isIos && !input.isStandalone && input.notificationPermission !== 'denied') return 'requires_install'
  if (input.notificationPermission === 'granted') return 'granted'
  if (input.notificationPermission === 'denied') return 'denied'
  return 'default'
}
